





































































































































































































































































































































































import { mailMergeService } from '@/services'
import { MailMergeTemplate, TemplateDataSource } from '@/types/MailMergeTypes'
import saveAs from 'file-saver'
import Vue from 'vue'
import MailMergeTemplateDialog from './MailMergeTemplateDialog.vue'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import editorHtml from '!!raw-loader!@stackdc/docxeditor/dist/index.html'

type TableOptions = {
    page: number
    itemsPerPage: number
    sortBy: string[]
    sortDesc: boolean[]
}

type SnackbarState = {
    show: boolean
    message: string
    color: 'success' | 'error'
}

export default Vue.extend({
    name: 'MailMergeTemplateManager',

    components: { MailMergeTemplateDialog },

    props: {
        /**
         * Available data-source fields passed through to the create/edit dialog.
         */
        dataSourceField: {
            type: Object as () => TemplateDataSource,
            required: true,
        },
    },

    data() {
        return {
            // Manager visibility
            dialogOpen: false,

            // Template list
            templates: [] as MailMergeTemplate[],
            totalCount: 0,
            loading: false,
            fetchError: null as string | null,
            search: '',
            searchDebounceTimer: null as ReturnType<typeof setTimeout> | null,
            tableOptions: {
                page: 1,
                itemsPerPage: 10,
                sortBy: [],
                sortDesc: [],
            } as TableOptions,

            // Create / Edit
            templateDialogOpen: false,
            selectedTemplate: null as MailMergeTemplate | null,

            // Delete
            deleteDialogOpen: false,
            templateToDelete: null as MailMergeTemplate | null,
            deleteLoading: false,

            // Download (tracks which row is downloading)
            downloadingId: null as string | null,
            downloadLoading: false,

            // Feedback
            snackbar: {
                show: false,
                message: '',
                color: 'success',
            } as SnackbarState,

            editorUrl: URL.createObjectURL(new Blob([editorHtml], { type: 'text/html' }))
        }
    },
    beforeDestroy() {
        URL.revokeObjectURL(this.editorUrl)
    },
    computed: {
        headers(): any[] {
            return [
                {
                    text: 'Name',
                    value: 'name',
                    sortable: false,
                },
                {
                    text: 'Mode',
                    value: 'mode',
                    sortable: false,
                    width: '140px',
                },
                {
                    text: 'Page',
                    value: 'pageName',
                    sortable: false,
                    width: '160px',
                },
                {
                    text: 'Last Updated',
                    value: 'lastUpdated',
                    sortable: false,
                    width: '150px',
                },
                {
                    text: 'Last Updated By',
                    value: 'lastUpdatedBy',
                    sortable: false,
                    width: '150px',
                },
                {
                    text: '',
                    value: 'actions',
                    sortable: false,
                    align: 'end',
                    width: '120px',
                },
            ]
        },

        tableFooterProps(): any {
            return {
                'items-per-page-options': [5, 10, 25],
                'show-current-page': true,
            }
        },
    },

    watch: {
        dialogOpen(val: boolean) {
            if (val) {
                this.resetState()
                this.fetchTemplates()
            }
        },

        templateDialogOpen(val: boolean) {
            if (!val) {
                // Refresh after create/edit dialog closes (saved or cancelled)
                this.fetchTemplates()
            }
        },
    },

    methods: {
        // ── Visibility ───────────────────────────────────────────────────────

        openManager() {
            this.dialogOpen = true
        },

        onClose() {
            this.dialogOpen = false
        },

        resetState() {
            this.search = ''
            this.fetchError = null
            this.templates = []
            this.totalCount = 0
            this.tableOptions = {
                page: 1,
                itemsPerPage: 10,
                sortBy: [],
                sortDesc: [],
            }
        },

        // ── Data fetching ────────────────────────────────────────────────────

        async fetchTemplates() {
            if (!this.dialogOpen) return
            this.loading = true
            this.fetchError = null
            try {
                const result = await mailMergeService.getTemplates(
                    this.tableOptions.page,
                    this.tableOptions.itemsPerPage,
                    this.dataSourceField.pageName,
                    this.search || undefined,
                )
                this.templates = result.records
                this.totalCount = result.totalRecords
            } catch {
                this.fetchError = 'Failed to load templates. Please try again.'
            } finally {
                this.loading = false
            }
        },

        onTableOptionsChanged(opts: TableOptions) {
            const prev = this.tableOptions
            this.tableOptions = opts
            // Only re-fetch when pagination actually changes (not on table mount)
            if (
                opts.page !== prev.page ||
                opts.itemsPerPage !== prev.itemsPerPage
            ) {
                this.fetchTemplates()
            }
        },

        onSearchInput() {
            if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
            this.searchDebounceTimer = setTimeout(() => {
                this.tableOptions = { ...this.tableOptions, page: 1 }
                this.fetchTemplates()
            }, 350)
        },

        onSearchClear() {
            this.search = ''
            this.tableOptions = { ...this.tableOptions, page: 1 }
            this.fetchTemplates()
        },

        // ── Create / Edit ────────────────────────────────────────────────────

        onCreate() {
            this.selectedTemplate = null
            this.templateDialogOpen = true
        },

        onEdit(item: MailMergeTemplate) {
            this.selectedTemplate = {
                ...item
            }
            this.templateDialogOpen = true
        },

        // ── Delete ───────────────────────────────────────────────────────────

        onDeleteRequest(item: MailMergeTemplate) {
            this.templateToDelete = item
            this.deleteDialogOpen = true
        },

        async confirmDelete() {
            if (!this.templateToDelete) return
            this.deleteLoading = true
            const name = this.templateToDelete.name
            try {
                await mailMergeService.deleteTemplate(this.templateToDelete.id ?? '')
                this.deleteDialogOpen = false
                this.templateToDelete = null
                this.showSnackbar(`"${name}" deleted successfully.`, 'success')
                // If the last item on a page was deleted, go back one page
                if (this.templates.length === 1 && this.tableOptions.page > 1) {
                    this.tableOptions = { ...this.tableOptions, page: this.tableOptions.page - 1 }
                }
                await this.fetchTemplates()
            } catch {
                this.showSnackbar('Failed to delete the template. Please try again.', 'error')
            } finally {
                this.deleteLoading = false
            }
        },

        // ── Download ─────────────────────────────────────────────────────────

        async onDownload(item: MailMergeTemplate) {
            this.downloadingId = item.id ?? null
            this.downloadLoading = true
            try {
                const result = await mailMergeService.downloadTemplate(item.id ?? '')
                // Handle the result as needed
                saveAs(result.data, item.name || 'template.docx')
            } catch (error) {
                // Handle the error as needed
                this.showSnackbar('Failed to download the template. Please try again.', 'error')
            } finally {
                this.downloadingId = null
                this.downloadLoading = false
            }
        },
        showSnackbar(message: string, color: 'success' | 'error') {
            this.snackbar = { show: true, message, color }
        },
    },
})
